'use client';

import { Trans, useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { AssociationFileDialog } from './AssociationFileDialog';

/**
 * Operator instructions for putting a merchant domain live on Apple Pay.
 *
 * The steps are ordered because the order is load-bearing: Apple reads the
 * verification file during registration, and Basis Theory validates the
 * certificate against a domain Apple has already accepted. Doing them out of
 * order fails in ways that read as healthy afterwards.
 *
 * Step 5 exists because of the 2026-08-14 incident: the payment config is baked
 * into the checkout page when the funnel is saved, so a domain can be
 * registered, live and correct in every table while shoppers are still served
 * the previous configuration. Saving the Studio design does not do it.
 */
const STEPS = ['step1', 'step2', 'step3', 'step4', 'step5'] as const;
const FILE_RULES = ['rule1', 'rule2', 'rule3', 'rule4', 'rule5'] as const;

interface HowToAddDomainProps {
  /** Lets step 2 open the verification file inline. Absent until the integrator is configured. */
  associationFileContent?: string | null;
}

export function HowToAddDomain({ associationFileContent }: HowToAddDomainProps) {
  const { t } = useTranslation('manageApplePay');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('howTo.title')}</CardTitle>
        <CardDescription>{t('howTo.intro')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ol className="space-y-4">
          {STEPS.map((step, index) => (
            <li key={step} className="flex gap-3">
              <span className="bg-muted text-muted-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium">
                {index + 1}
              </span>
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-medium leading-6">{t(`howTo.${step}.title`)}</p>

                {step === 'step2' ? (
                  <div className="space-y-2">
                    <p className="text-muted-foreground text-sm">
                      {/* The file is reachable from the sentence telling you to send it,
                          rather than only from the panel further up the page. */}
                      <Trans
                        i18nKey="howTo.step2.body"
                        ns="manageApplePay"
                        components={{
                          file: associationFileContent ? (
                            <AssociationFileDialog
                              content={associationFileContent}
                              trigger={
                                <button
                                  type="button"
                                  className="text-primary font-medium underline underline-offset-2"
                                />
                              }
                            />
                          ) : (
                            <span className="font-medium" />
                          ),
                        }}
                      />
                    </p>
                    <p className="text-muted-foreground text-sm">{t('howTo.step2.rulesIntro')}</p>
                    <code className="bg-muted block overflow-x-auto rounded px-2 py-1.5 font-mono text-xs">
                      {t('howTo.step2.path')}
                    </code>
                    <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
                      {FILE_RULES.map((rule) => (
                        <li key={rule}>{t(`howTo.step2.${rule}`)}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">{t(`howTo.${step}.body`)}</p>
                )}
              </div>
            </li>
          ))}
        </ol>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>{t('howTo.verifyTitle')}</AlertTitle>
          <AlertDescription>{t('howTo.verifyBody')}</AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}
